import apiService from './api';
import { offlineStorage, SyncResult, SyncQueueItem } from './offlineStorage';
import { Pesée, Ticket } from '@/types/weighing';

export interface ConflictResolution {
  localData: any;
  serverData: any;
  resolution: 'local' | 'server' | 'merge';
  mergedData?: any;
}

export interface SyncOptions {
  forceSync?: boolean;
  skipConfirmation?: boolean;
  syncType?: 'full' | 'incremental';
}

export class SyncService {
  private isSyncing = false;
  private syncInProgress = false;
  private conflictResolver: ConflictResolver;

  constructor() {
    this.conflictResolver = new ConflictResolver();
  }

  // Méthode principale de synchronisation
  async syncData(_options: SyncOptions = {}): Promise<SyncResult> {
    if (this.isSyncing) {
      throw new Error('Une synchronisation est déjà en cours');
    }

    if (!navigator.onLine) {
      throw new Error('Pas de connexion internet disponible');
    }

    this.isSyncing = true;
    this.syncInProgress = true;
    
    // Émettre l'événement de début de synchronisation
    window.dispatchEvent(new CustomEvent('sync-start'));

    const startTime = Date.now();
    const result: SyncResult = {
      uploaded: 0,
      downloaded: 0,
      errors: [],
      conflicts: 0,
      duration: 0
    };

    try {
      console.log('🔄 Début de la synchronisation...');

      // 1. Télécharger les données de référence
      await this.downloadReferenceData(result);

      // 2. Synchroniser les pesées
      await this.syncPesees(result);

      // 3. Synchroniser les tickets
      await this.syncTickets(result);

      // 4. Uploader les changements locaux
      await this.uploadLocalChanges(result);

      // 5. Mettre à jour les métadonnées
      await offlineStorage.setLastSyncDate(new Date());

      result.duration = Date.now() - startTime;
      
      console.log('✅ Synchronisation terminée:', result);
      
      // Émettre l'événement de succès
      window.dispatchEvent(new CustomEvent('sync-success', { detail: result }));

    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation:', error);
      result.errors.push(error instanceof Error ? error.message : 'Erreur inconnue');
      
      // Émettre l'événement d'erreur
      window.dispatchEvent(new CustomEvent('sync-error', { detail: error }));
    } finally {
      this.isSyncing = false;
      this.syncInProgress = false;
      
      // Émettre l'événement de fin de synchronisation
      window.dispatchEvent(new CustomEvent('sync-complete', { detail: result }));
    }

    return result;
  }

  // Télécharger les données de référence
  private async downloadReferenceData(result: SyncResult): Promise<void> {
    console.log('📥 Téléchargement des données de référence...');

    const referenceEndpoints = [
      { endpoint: '/weighing/api/camions/', store: 'camions' },
      { endpoint: '/weighing/api/chauffeurs/', store: 'chauffeurs' },
      { endpoint: '/weighing/api/produits/', store: 'produits' },
      { endpoint: '/weighing/api/clients/', store: 'clients' },
      { endpoint: '/weighing/api/fournisseurs/', store: 'fournisseurs' },
      { endpoint: '/weighing/api/exportateurs/', store: 'exportateurs' },
      { endpoint: '/weighing/api/transporteurs/', store: 'transporteurs' }
    ];

    for (const { endpoint, store } of referenceEndpoints) {
      try {
        const response = await apiService.get(endpoint);
        const data = response.data.results || response.data;
        
        // Sauvegarder chaque élément individuellement
        if (Array.isArray(data)) {
          for (const item of data) {
            await offlineStorage.saveData(store, item);
          }
        }
        
        result.downloaded += Array.isArray(data) ? data.length : 1;
        console.log(`✅ ${store} téléchargés: ${Array.isArray(data) ? data.length : 1} éléments`);
      } catch (error) {
        console.error(`❌ Erreur téléchargement ${store}:`, error);
        result.errors.push(`Erreur téléchargement ${store}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      }
    }
  }

  // Synchroniser les pesées
  private async syncPesees(result: SyncResult): Promise<void> {
    console.log('🔄 Synchronisation des pesées...');

    try {
      // Récupérer les pesées depuis le serveur
      const response = await apiService.get('/weighing/api/pesees/', {
        params: {
          include_relations: 'true',
          expand: 'camion,chauffeur,produit,client,fournisseur,exportateur',
          page_size: 1000 // Récupérer plus de données
        }
      });

      const serverPesees = response.data.results || response.data;
      
      // Récupérer les pesées locales
      const localPesees = await offlineStorage.getPesees();
      
      // Fusionner les données
      const mergedPesees = this.mergePesees(localPesees, serverPesees);
      
      // Sauvegarder les pesées fusionnées
      for (const pesee of mergedPesees) {
        await offlineStorage.savePesee(pesee);
      }
      
      result.downloaded += serverPesees.length;
      console.log(`✅ Pesées synchronisées: ${mergedPesees.length} éléments`);
      
    } catch (error) {
      console.error('❌ Erreur synchronisation pesées:', error);
      result.errors.push(`Erreur synchronisation pesées: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }

  // Synchroniser les tickets
  private async syncTickets(result: SyncResult): Promise<void> {
    console.log('🔄 Synchronisation des tickets...');

    try {
      const response = await apiService.get('/weighing/api/tickets/', {
        params: { page_size: 1000 }
      });

      const serverTickets = response.data.results || response.data;
      const localTickets = await offlineStorage.getTickets();
      
      const mergedTickets = this.mergeTickets(localTickets, serverTickets);
      
      for (const ticket of mergedTickets) {
        await offlineStorage.saveTicket(ticket);
      }
      
      result.downloaded += serverTickets.length;
      console.log(`✅ Tickets synchronisés: ${mergedTickets.length} éléments`);
      
    } catch (error) {
      console.error('❌ Erreur synchronisation tickets:', error);
      result.errors.push(`Erreur synchronisation tickets: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }

  // Uploader les changements locaux
  private async uploadLocalChanges(result: SyncResult): Promise<void> {
    console.log('📤 Upload des changements locaux...');

    const pendingItems = await offlineStorage.getPendingSyncItems();
    
    for (const item of pendingItems) {
      try {
        await this.processSyncItem(item);
        await offlineStorage.removeSyncItem(item.id);
        result.uploaded++;
        
        console.log(`✅ Item synchronisé: ${item.type} ${item.endpoint}`);
      } catch (error) {
        console.error(`❌ Erreur synchronisation item ${item.id}:`, error);
        result.errors.push(`Erreur synchronisation ${item.type}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
        
        // Incrémenter le compteur de retry
        item.retryCount++;
        if (item.retryCount < 3) {
          await offlineStorage.saveData('sync_queue', item);
        } else {
          console.error(`❌ Item ${item.id} abandonné après 3 tentatives`);
        }
      }
    }
  }

  // Traiter un élément de la queue de synchronisation
  private async processSyncItem(item: SyncQueueItem): Promise<void> {
    await offlineStorage.updateSyncItemStatus(item.id, 'syncing');

    switch (item.type) {
      case 'CREATE':
        await apiService.post(item.endpoint, item.data);
        break;
      case 'UPDATE':
        await apiService.patch(`${item.endpoint}${item.data.id}/`, item.data);
        break;
      case 'DELETE':
        await apiService.delete(`${item.endpoint}${item.data.id}/`);
        break;
      default:
        throw new Error(`Type de synchronisation non supporté: ${item.type}`);
    }
  }

  // Fusionner les pesées locales et serveur
  private mergePesees(localPesees: Pesée[], serverPesees: Pesée[]): Pesée[] {
    const mergedMap = new Map<string, Pesée>();
    
    // Ajouter les pesées serveur
    serverPesees.forEach(pesee => {
      mergedMap.set(pesee.id.toString(), pesee);
    });
    
    // Fusionner avec les pesées locales
    localPesees.forEach(localPesee => {
      const serverPesee = mergedMap.get(localPesee.id.toString());
      
      if (serverPesee) {
        // Résoudre les conflits
        const resolution = this.conflictResolver.resolvePeseeConflict(localPesee, serverPesee);
        mergedMap.set(localPesee.id.toString(), resolution.mergedData || serverPesee);
      } else if ((localPesee as any).isOffline) {
        // Pesée créée offline, la garder
        mergedMap.set(localPesee.id.toString(), localPesee);
      }
    });
    
    return Array.from(mergedMap.values());
  }

  // Fusionner les tickets locaux et serveur
  private mergeTickets(localTickets: Ticket[], serverTickets: Ticket[]): Ticket[] {
    const mergedMap = new Map<string, Ticket>();
    
    serverTickets.forEach(ticket => {
      mergedMap.set(ticket.id.toString(), ticket);
    });
    
    localTickets.forEach(localTicket => {
      const serverTicket = mergedMap.get(localTicket.id.toString());
      
      if (serverTicket) {
        const resolution = this.conflictResolver.resolveTicketConflict(localTicket, serverTicket);
        mergedMap.set(localTicket.id.toString(), resolution.mergedData || serverTicket);
      } else if ((localTicket as any).isOffline) {
        mergedMap.set(localTicket.id.toString(), localTicket);
      }
    });
    
    return Array.from(mergedMap.values());
  }

  // Ajouter un élément à la queue de synchronisation
  async addToSyncQueue(type: SyncQueueItem['type'], endpoint: string, data: any): Promise<string> {
    const item: Omit<SyncQueueItem, 'id'> = {
      type,
      endpoint,
      data,
      timestamp: new Date(),
      retryCount: 0,
      status: 'pending'
    };

    const id = await offlineStorage.addToSyncQueue(item);
    
    // Émettre l'événement de changement de queue
    window.dispatchEvent(new CustomEvent('sync-queue-changed'));
    
    return id;
  }

  // Obtenir le statut de synchronisation
  getSyncStatus(): { isSyncing: boolean; syncInProgress: boolean } {
    return {
      isSyncing: this.isSyncing,
      syncInProgress: this.syncInProgress
    };
  }

  // Forcer une synchronisation complète
  async forceFullSync(): Promise<SyncResult> {
    console.log('🔄 Synchronisation complète forcée...');
    
    // Vider le cache local
    await offlineStorage.clearAllData();
    
    // Relancer une synchronisation complète
    return this.syncData({ forceSync: true, syncType: 'full' });
  }

  // Synchronisation incrémentale
  async incrementalSync(): Promise<SyncResult> {
    console.log('🔄 Synchronisation incrémentale...');
    
    const lastSync = await offlineStorage.getLastSyncDate();
    if (!lastSync) {
      return this.syncData({ syncType: 'full' });
    }
    
    // Synchroniser seulement les changements depuis la dernière sync
    return this.syncData({ syncType: 'incremental' });
  }
}

// Classe pour résoudre les conflits
class ConflictResolver {
  resolvePeseeConflict(localPesee: Pesée, serverPesee: Pesée): ConflictResolution {
    // Stratégie de résolution pour les pesées
    const localDate = new Date(localPesee.updated_at || localPesee.date_heure || localPesee.created_at);
    const serverDate = new Date(serverPesee.updated_at || serverPesee.date_heure || serverPesee.created_at);
    
    if (localDate > serverDate) {
      // Les données locales sont plus récentes
      return {
        localData: localPesee,
        serverData: serverPesee,
        resolution: 'local',
        mergedData: localPesee
      };
    } else {
      // Fusionner les observations
      const mergedObservations = this.mergeObservations(
        serverPesee.observations || '',
        localPesee.observations || ''
      );
      
      return {
        localData: localPesee,
        serverData: serverPesee,
        resolution: 'merge',
        mergedData: {
          ...serverPesee,
          observations: mergedObservations,
          updated_at: new Date().toISOString()
        }
      };
    }
  }

  resolveTicketConflict(localTicket: Ticket, serverTicket: Ticket): ConflictResolution {
    // Stratégie de résolution pour les tickets
    const localDate = new Date(localTicket.created_at);
    const serverDate = new Date(serverTicket.created_at);
    
    if (localDate > serverDate) {
      return {
        localData: localTicket,
        serverData: serverTicket,
        resolution: 'local',
        mergedData: localTicket
      };
    } else {
      return {
        localData: localTicket,
        serverData: serverTicket,
        resolution: 'server',
        mergedData: serverTicket
      };
    }
  }

  private mergeObservations(serverObservations: string, localObservations: string): string {
    if (!localObservations) return serverObservations;
    if (!serverObservations) return localObservations;
    
    const serverLines = serverObservations.split('\n').filter(line => line.trim());
    const localLines = localObservations.split('\n').filter(line => line.trim());
    
    // Éviter les doublons
    const uniqueLines = [...new Set([...serverLines, ...localLines])];
    
    return uniqueLines.join('\n');
  }
}

// Instance singleton
export const syncService = new SyncService();
